import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiMaximize, FiMinimize } = FiIcons;

const CropModal = ({ image, aspect, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropChange = useCallback((crop) => setCrop(crop), []);
  const onZoomChange = useCallback((zoom) => setZoom(zoom), []);
  
  const onCropAreaChange = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Calculates the zoom level required to fit the entire image within the crop area
   * (Object-fit: contain logic)
   */
  const onMediaLoaded = useCallback((mediaSize) => {
    const { width, height } = mediaSize;
    const imageAspect = width / height;
    
    // Calculate the zoom required to fit the longer dimension
    const fitZoom = imageAspect > aspect ? aspect / imageAspect : imageAspect / aspect;
    
    // Set both minZoom and initial zoom to this "contain" value
    setMinZoom(fitZoom);
    setZoom(fitZoom);
  }, [aspect]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const croppedImageFile = await getCroppedImg(image, croppedAreaPixels);
      onCropComplete(croppedImageFile);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-xl flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-2xl rounded-[3rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
      >
        <div className="p-8 border-b border-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-black text-slate-900 tracking-tighter">Perfect Your Frame</h3>
            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mt-1">Drag and zoom to adjust</p>
          </div>
          <button 
            type="button" 
            onClick={onCancel}
            className="p-3 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-2xl transition-all"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="relative flex-1 bg-slate-100 min-h-[400px]">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={minZoom + 3}
            aspect={aspect}
            onCropChange={onCropChange}
            onCropComplete={onCropAreaChange}
            onZoomChange={onZoomChange}
            onMediaLoaded={onMediaLoaded}
            restrictPosition={false} // Allows showing the full image even if aspect ratios differ
          />
        </div>

        <div className="p-8 space-y-8 bg-white">
          <div className="flex items-center gap-6">
            <SafeIcon icon={FiMinimize} className="text-slate-400" />
            <input 
              type="range" 
              value={zoom}
              min={minZoom}
              max={minZoom + 3}
              step={0.01}
              aria-labelledby="Zoom"
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <SafeIcon icon={FiMaximize} className="text-slate-400" />
          </div>

          <button 
            type="button" 
            onClick={handleConfirm}
            className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center"
          >
            <SafeIcon icon={FiCheck} className="mr-3" />
            Apply Crop & Upload
          </button>
        </div>
      </motion.div>
    </div>
  );
};

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => (image.onload = resolve));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const file = new File([blob], 'cropped-image.webp', { type: 'image/webp' });
      resolve(file);
    }, 'image/webp', 0.95);
  });
}

export default CropModal;